import type { RichTextContentProtocol, RichTextValue } from '@lighthouse/core'
import { Modal } from '@mantine/core'
import type { JSONContent } from '@tiptap/core'
import React, { useCallback, useMemo, useState } from 'react'

import { useApplicationContext, useSpaceQuotaContext } from '../../../../../contexts'
import { RichTextEditor, RichTextRenderer } from '../../../../RichText'
import type { ToolbarProps } from '../../../../RichText/Toolbar'
import { useTokenRefreshManager } from '../../../../UploadManage'
import type { FieldBaseProps } from '../../../types'
import * as SC from './styles'

interface NotesModalFieldProps extends FieldBaseProps {
    value?: RichTextValue
}

export const NotesModalField: React.FunctionComponent<NotesModalFieldProps> = props => {
    const {
        readOnly,
        onChange,
        value,
        isControlled,
        record,
        config: { title, richTextUploadOptions, fieldPointer, placeholder, breakPoint }
    } = props
    const tokenRefreshManager = useTokenRefreshManager()
    const { pageTarget, appId } = useApplicationContext()
    const { quota } = useSpaceQuotaContext()

    const [opened, setOpened] = useState(false)

    const [data, setData] = useState<RichTextContentProtocol | null>(value ?? null)

    const usedValue = useMemo(() => (isControlled ? value : data), [isControlled, data, value])

    const fontFamily = useMemo(() => breakPoint?.font?.fontFamily, [breakPoint?.font?.fontFamily])

    const config: ToolbarProps['config'] = useMemo(
        () => ({
            mode: 'all',
            placeholder,
            image: {
                uploadOptions: richTextUploadOptions,
                appId,
                tokenRefreshManager: tokenRefreshManager || undefined,
                maxSize: quota?.uploadFileLimitSize
            },
            video: {
                uploadOptions: richTextUploadOptions,
                appId,
                tokenRefreshManager: tokenRefreshManager || undefined,
                maxSize: quota?.uploadFileLimitSize
            }
        }),
        [placeholder, richTextUploadOptions, appId, tokenRefreshManager, quota?.uploadFileLimitSize]
    )

    const handleChange = useCallback(
        (json: JSONContent | null, isFinish?: boolean) => {
            setData(json)
            if (isFinish) {
                setOpened(false)
                if (json) {
                    onChange?.({ type: 'notes', value: json })
                }
            }
        },
        [onChange]
    )

    return (
        <SC.Container
            data-input-type="notes"
            data-field-input
            data-field-active={false}
            data-field-disabled={Boolean(readOnly)}
            readOnly={readOnly}
        >
            <SC.RichTextWrapper onClick={() => !readOnly && setOpened(true)}>
                <RichTextRenderer
                    key={`${record?.id}-${fieldPointer}`}
                    style={{
                        minHeight: readOnly ? undefined : 200,
                        padding: readOnly ? 0 : 8,
                        cursor: 'text'
                    }}
                    config={config}
                    value={usedValue}
                />
            </SC.RichTextWrapper>

            <Modal
                opened={opened}
                target={pageTarget}
                styles={{
                    root: {
                        position: 'absolute',
                        fontFamily: fontFamily || 'inherit'
                    },
                    modal: {
                        width: '100%',
                        height: '100%',
                        borderBottomLeftRadius: pageTarget ? 55 : 0,
                        borderBottomRightRadius: pageTarget ? 55 : 0
                    },
                    inner: {
                        padding: 0
                    },
                    close: {
                        display: 'none'
                    },
                    overlay: {
                        borderRadius: pageTarget ? '55px!important' : 0
                    },
                    header: {
                        marginBottom: 0
                    },
                    body: {
                        position: 'relative',
                        height: '100%'
                    }
                }}
                padding={0}
                closeOnEscape
                data-ignore-click-away
                zIndex={201}
                onClose={() => setOpened(false)}
            >
                <SC.Header>
                    <SC.LeftExtra onClick={() => setOpened(false)}>
                        <SC.Icon type="Close" size={24} />
                    </SC.LeftExtra>
                    <SC.Title size={16}>{title}</SC.Title>
                    <SC.RightFill>
                        <SC.Text size={16} color="var(--color-main)" onClick={() => handleChange(data, true)} style={{ fontWeight: 600 }}>
                            完成
                        </SC.Text>
                    </SC.RightFill>
                </SC.Header>

                <SC.EditorContent>
                    <RichTextEditor
                        config={config}
                        readonly={readOnly}
                        key={`${record?.id}-${fieldPointer}`}
                        value={usedValue}
                        placeholder={placeholder}
                        onChange={val => handleChange(val)}
                        isMobile
                        styles={{
                            editorContainer: {
                                minHeight: `100%`
                            },
                            editorContent: {
                                flex: 1
                            },
                            toolbarContainer: {
                                flexWrap: 'wrap'
                            },
                            toolbarMenuList: {
                                // flexWrap: 'wrap',
                                overflow: 'auto hidden',
                                scrollbarWidth: 'none'
                            }
                        }}
                    />
                </SC.EditorContent>
            </Modal>
        </SC.Container>
    )
}
